import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";

import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";

import DemandTab from "@plannel/pages/factory-plan/master/order/DemandTab";
import WorkOrderTab from "@plannel/pages/factory-plan/master/order/WorkOrderTab";

// 이 화면의 고정 조회조건 라벨 키(§10 — DB 동적 표시 제어 제거, fp-master-screen-guide.md §6-1).
const SEARCH_FIELD_LABELS = {
  customerCdNm: "FP_CUSTOMER",
  inventoryCdNm: "FP_INVENTORY",
  dmndCdNm: "FP_DEMAND",
  woCdNm: "FP_WORK_ORDER",
};

// old tabSrhOptsInfo 대응 — 탭별로 노출할 조회조건 필드 목록만 다르게(§11-2).
const TAB_SEARCH_FIELDS = [
  ["customerCdNm", "inventoryCdNm", "dmndCdNm"], // 0: 판매주문(Demand) 탭
  ["inventoryCdNm", "dmndCdNm", "woCdNm"],        // 1: 작업주문(Work Order) 탭
];

// 다른 화면(validation/Validation) → 판매주문/작업주문 탭 자동 전환 + 검색값 프리필(딥링크).
// item/Item.js와 동일한 historyState(redux) 패턴 — 상세 설명은 그쪽 주석 참고.
const Order = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [tabIndex, setTabIndex] = useState(reduxViewState?.tabIndex ?? 0);
  const [searchFields, setSearchFields] = useState({
    customerCdNm: reduxViewState?.customerCdNm ?? "",
    inventoryCdNm: reduxViewState?.inventoryCdNm ?? "",
    dmndCdNm: reduxViewState?.dmndCdNm ?? "",
    woCdNm: reduxViewState?.woCdNm ?? "",
  });

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, searchFields]);

  // item/Item.js와 동일한 딥링크 수신 로직(historyState 감지 → viewState 재조회).
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    if (freshState.planScope) setPlanScope(freshState.planScope);
    if (freshState.tabIndex !== undefined) setTabIndex(freshState.tabIndex);
    setSearchFields((prev) => ({
      customerCdNm: freshState.customerCdNm ?? prev.customerCdNm,
      inventoryCdNm: freshState.inventoryCdNm ?? prev.inventoryCdNm,
      dmndCdNm: freshState.dmndCdNm ?? prev.dmndCdNm,
      woCdNm: freshState.woCdNm ?? prev.woCdNm,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  const onSearchFieldChange = (tpCd, value) => setSearchFields(prev => ({ ...prev, [tpCd]: value }));

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={TAB_SEARCH_FIELDS[tabIndex]}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        {/* FilterContainer는 마지막 자식을 float:right(+필터 토글 아이콘)로 띄운다 — SearchOptsGroup이
            마지막이면 PlanScope와 큰 간격이 생기므로, 빈 Box를 마지막에 둬 그 역할을 넘긴다. */}
        <Box />
      </FilterContainer>
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 38 }}
      >
        <Tab label={t("FP_DEMAND")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
        <Tab label={t("FP_WORK_ORDER")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
      </Tabs>
      {/* §11-1: 탭은 항상 마운트하고 display로만 숨긴다 — 그리드 dirty-row/스크롤/선택 상태 보존. */}
      <Box sx={{ display: tabIndex === 0 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <DemandTab viewName={`${viewName}_DEMAND`} planScope={planScope} searchFields={searchFields} />
      </Box>
      <Box sx={{ display: tabIndex === 1 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <WorkOrderTab viewName={`${viewName}_WORK_ORDER`} planScope={planScope} searchFields={searchFields} />
      </Box>
    </>
  );
};

export default withTranslation()(Order);
